import React, { useCallback, useEffect, useMemo, useState } from 'react';

import ClaimFlagChips from '~/components/ClaimFlags/ClaimFlagChips';
import useClaimFlagConfigurations from '~/components/ClaimFlags/hooks/useClaimFlagConfigurations';
import { isFlagInClaimScope } from '~/components/ClaimFlags/isFlagInClaimScope';
import type { ClaimFlagConfiguration } from '~/components/ClaimFlags/types';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import ColoredDot from '~/components/core/Atomic/ColoredDot';
import Menu from '~/components/core/Atomic/Menu';
import MenuItem from '~/components/core/Atomic/MenuItem';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import useOrganization from '~/components/OrganizationContext';
import { reportAxiosError } from '~/Utils';

import { applyClaimFlags, removeClaimFlag } from './apiService';

type ClaimFlagsViewAndUpdateProps = {
  claimId: number;
  appliedFlagKeys?: string[] | null;
  claimLob: string;
  claimSubOrganizationId?: number | null;
  onUpdate?: () => void | Promise<void>;
};

/** Dot + label for the picker's dropdown rows (untinted, unbounded — no width cap; applied chips are
 *  tinted and omit the dot). */
const FlagChipLabel = ({ config }: { config: ClaimFlagConfiguration }) => (
  <div className="flex items-center gap-8">
    <ColoredDot color={config.color} size="XS" />
    {config.label}
  </div>
);

const ClaimFlagsViewAndUpdate = ({
  claimId,
  appliedFlagKeys,
  claimLob,
  claimSubOrganizationId,
  onUpdate,
}: ClaimFlagsViewAndUpdateProps) => {
  const { claimFlagConfigurations } = useClaimFlagConfigurations();
  const { subOrganizationEnabled } = useOrganization() as { subOrganizationEnabled?: boolean };
  const [currentKeys, setCurrentKeys] = useState<string[]>(appliedFlagKeys ?? []);
  const [isUpdating, setIsUpdating] = useState(false);
  const [pickerAnchor, setPickerAnchor] = useState<HTMLButtonElement | null>(null);

  // Re-sync from the claim whenever its applied set changes (e.g. after onUpdate refetches).
  const appliedSignature = JSON.stringify(appliedFlagKeys ?? []);
  useEffect(() => {
    setCurrentKeys(appliedFlagKeys ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedSignature]);

  // Optimistically apply nextKeys, then persist. Roll back ONLY if the mutation itself fails — a
  // successful apply/remove whose follow-up refetch throws keeps the optimistic order (mirrors the
  // claim-stage pattern, but without conflating mutate failure with refetch failure).
  const runMutation = useCallback(
    async (nextKeys: string[], mutate: () => Promise<void>) => {
      const fallbackKeys = currentKeys;
      setCurrentKeys(nextKeys);
      setIsUpdating(true);
      try {
        await mutate();
      } catch (error) {
        reportAxiosError(error);
        setCurrentKeys(fallbackKeys);
        setIsUpdating(false);
        return;
      }
      try {
        if (onUpdate) {
          await onUpdate();
        }
      } catch (error) {
        reportAxiosError(error);
      } finally {
        setIsUpdating(false);
      }
    },
    [currentKeys, onUpdate]
  );

  const handleAdd = useCallback(
    (flagKey: string) => {
      setPickerAnchor(null);
      return runMutation([...currentKeys, flagKey], () => applyClaimFlags(claimId, [flagKey]));
    },
    [claimId, currentKeys, runMutation]
  );

  const handleRemove = useCallback(
    (flagKey: string) =>
      runMutation(
        currentKeys.filter((key) => key !== flagKey),
        () => removeClaimFlag(claimId, flagKey)
      ),
    [claimId, currentKeys, runMutation]
  );

  // Picker offers enabled, in-scope flags not already applied.
  const pickerOptions = useMemo(
    () =>
      claimFlagConfigurations.filter(
        (config) =>
          config.is_enabled &&
          !currentKeys.includes(config.key) &&
          isFlagInClaimScope(config, {
            lob: claimLob,
            subOrganizationId: claimSubOrganizationId,
            subOrganizationEnabled,
          })
      ),
    [claimFlagConfigurations, currentKeys, claimLob, subOrganizationEnabled, claimSubOrganizationId]
  );

  const canAdd = pickerOptions.length > 0;

  return (
    <div className="flex items-center gap-2">
      <ClaimFlagChips flagKeys={currentKeys} onRemove={handleRemove} disabled={isUpdating} className="max-w-[200px]" />

      {canAdd && (
        <>
          {/* Sized to the 24px chip height so the row keeps a constant height when the button appears. */}
          <IconButton
            size="small"
            className="h-24 w-24 p-0"
            onClick={(event) => setPickerAnchor(event.currentTarget)}
            disabled={isUpdating}
            aria-label="Add flag"
            aria-haspopup="true"
            aria-controls={pickerAnchor ? 'claim-flags-picker-menu' : undefined}
            aria-expanded={Boolean(pickerAnchor)}
          >
            <AddIcon fontSize="small" />
          </IconButton>
          <Menu
            id="claim-flags-picker-menu"
            open={Boolean(pickerAnchor)}
            anchorEl={pickerAnchor}
            onClose={() => setPickerAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {pickerOptions.map((config) => (
              <MenuItem key={config.key} onClick={() => handleAdd(config.key)}>
                <FlagChipLabel config={config} />
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </div>
  );
};

export default ClaimFlagsViewAndUpdate;
